// Phone validation utilities for international phone numbers
// Zonal API requirements: 8-15 digits (excluding formatting)

export const phoneNumberRegex = /^[\+]?[0-9\s\-\(\)]{7,30}$/;

/**
 * Validates phone number format (allows +, digits, spaces, hyphens, parentheses)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if format is valid
 */
export function isValidPhoneFormat(phone) {
    if (!phone) return false;
    return phoneNumberRegex.test(phone);
}

/**
 * Counts digits in phone number (strips all non-digit characters except +)
 * @param {string} phone - Phone number to count digits from
 * @returns {number} - Number of digits
 */
export function countPhoneDigits(phone) {
    if (!phone) return 0;
    return phone.replace(/\D/g, '').length;
}

/**
 * Validates phone number has correct digit count (8-15 digits)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if digit count is valid
 */
export function isValidPhoneDigitCount(phone) {
    const digitCount = countPhoneDigits(phone);
    return digitCount >= 8 && digitCount <= 15;
}

/**
 * Full phone number validation (format + digit count)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if phone is valid
 */
export function isValidPhoneNumber(phone) {
    return isValidPhoneFormat(phone) && isValidPhoneDigitCount(phone);
}

/**
 * Sanitizes phone number for backend API (strips formatting, keeps + and digits)
 * @param {string} phone - Phone number to sanitize
 * @returns {string} - Sanitized phone number
 */
export function sanitizePhoneForAPI(phone) {
    if (!phone) return '';
    // Remove all non-digit characters except +
    return phone.replace(/[^\d\+]/g, '');
}

/**
 * Sanitizes phone input for frontend display (removes invalid characters)
 * @param {string} phone - Phone number to sanitize
 * @returns {string} - Sanitized phone number
 */
export function sanitizePhoneInput(phone) {
    if (!phone) return '';
    // Allow only +, digits, spaces, hyphens, parentheses
    return phone.trim().replace(/[^\d\+\s\-\(\)]/g, '');
}
